import React, { ReactNode, useRef } from 'react';
import {
  FaBookmark,
  FaChevronLeft,
  FaCog,
  FaExternalLinkAlt,
  FaHistory,
  FaLightbulb,
  FaQuestionCircle,
} from 'react-icons/fa';
import { MdAdd } from 'react-icons/md';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../localization/useAppTranslation';
import { ThemedSpinner } from '../ThemedSpinner';
import '../../styles/ChatSidebar.css';

export type SidebarPanel = (
  | 'history'
  | 'coach-suggestions'
  | 'saved-prompts'
  | 'settings'
  | 'guided-tours'
);

interface ChatSidebarProps {
  activePanel: SidebarPanel;
  isOpen: boolean;
  isNewSessionLoading: boolean;
  onCollapse: () => void;
  onSelectPanel: (panel: SidebarPanel) => void;
  onNewSession: () => void;
  titleAdornment?: ReactNode;
  children: ReactNode;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  activePanel,
  isOpen,
  isNewSessionLoading,
  onCollapse,
  onSelectPanel,
  onNewSession,
  titleAdornment,
  children,
}) => {
  const { t } = useTranslation('common');
  const { logout } = useAuth();
  const navigationButtonRefs = useRef<Partial<Record<SidebarPanel, HTMLButtonElement | null>>>({});
  const sidebarNavigation = [
    { panel: 'history' as const, label: t('sidebar.history'), title: t('sidebar.recentChats'), Icon: FaHistory },
    { panel: 'saved-prompts' as const, label: t('sidebar.savedPrompts'), title: t('sidebar.savedPrompts'), Icon: FaBookmark },
    { panel: 'coach-suggestions' as const, label: t('sidebar.coachSuggestions'), title: t('sidebar.coachSuggestions'), Icon: FaLightbulb },
    { panel: 'settings' as const, label: t('sidebar.settings'), title: t('sidebar.settings'), Icon: FaCog },
    { panel: 'guided-tours' as const, label: t('sidebar.help'), title: t('sidebar.guidedTours'), Icon: FaQuestionCircle },
  ];
  const activeNavigation = sidebarNavigation.find((item) => item.panel === activePanel);

  const handleNavigation = (panel: SidebarPanel) => {
    if (panel === activePanel && isOpen) {
      onCollapse();
      navigationButtonRefs.current[panel]?.focus();
      return;
    }

    onSelectPanel(panel);
  };

  const handleCollapse = () => {
    onCollapse();
    navigationButtonRefs.current[activePanel]?.focus();
  };

  return (
    <div
      className={`chat-sidebar ${isOpen ? 'is-open' : 'is-collapsed'}`}
      data-tour={`${activePanel}-panel`}
    >
      <nav className="sidebar-rail" aria-label={t('sidebar.ariaLabel')} data-tour="sidebar-rail">
        <button
          type="button"
          className="sidebar-nav-button sidebar-new-session-button"
          onClick={onNewSession}
          aria-label={t('sidebar.newSession')}
          aria-busy={isNewSessionLoading}
          title={t('sidebar.newSessionTitle')}
          disabled={isNewSessionLoading}
        >
          {isNewSessionLoading ? (
            <ThemedSpinner size="sm" ariaLabel={t('status.loading')} />
          ) : (
            <MdAdd aria-hidden="true" />
          )}
        </button>
        {sidebarNavigation.map(({ panel, label, Icon }) => {
          const isPanelActive = isOpen && panel === activePanel;

          return (
            <button
              key={panel}
              ref={(element) => {
                navigationButtonRefs.current[panel] = element;
              }}
              type="button"
              className={`sidebar-nav-button ${isPanelActive ? 'is-active' : ''}`}
              onClick={() => handleNavigation(panel)}
              data-tour-panel={panel}
              aria-label={label}
              aria-current={isPanelActive ? 'page' : undefined}
              title={label}
            >
              <Icon aria-hidden="true" />
            </button>
          );
        })}
        <button
          type="button"
          className="sidebar-nav-button sidebar-return-to-safety-center-button"
          onClick={() => { void logout(); }}
          aria-label={t('header.returnToSafetyCenter')}
          title={t('header.returnToSafetyCenter')}
        >
          <FaExternalLinkAlt aria-hidden="true" />
        </button>
      </nav>

      <aside
        className="sidebar-panel"
        aria-labelledby="sidebar-panel-title"
      >
        <div className="sidebar-panel-header">
          <div className="sidebar-panel-title">
            <h2 id="sidebar-panel-title">{activeNavigation?.title}</h2>
            {titleAdornment}
          </div>
          <button
            type="button"
            className="sidebar-collapse-button"
            onClick={handleCollapse}
            aria-label={t('sidebar.collapse')}
            title={t('sidebar.collapse')}
          >
            <FaChevronLeft aria-hidden="true" />
          </button>
        </div>
        <div className="sidebar-panel-body">{children}</div>
      </aside>
    </div>
  );
};
